import type { Map as MlMap } from "maplibre-gl";
import { isLight } from "./theme";

/**
 * v0.9.9 A4 — one fixed vertical group at the top right (under the search box on phones, at the
 * map's right edge on desktop): 🇹🇭 ทั้งประเทศ / 📍 รอบตัวฉัน · 🌦 สภาพอากาศ · 🌀 พายุรอบโลก · ☀️/🌙 (B1).
 * Round 48 px buttons with a short caption, always in the same place; the day bars and the 〰 chip
 * leave this column free (CSS / river-chip.ts), so nothing overlaps in any state.
 */
export interface TopRightHandles {
  country: HTMLButtonElement; weather: HTMLButtonElement; monsoon: HTMLButtonElement; storm: HTMLButtonElement; theme: HTMLButtonElement; stack: HTMLElement;
  setCountry(mode: "country" | "back" | "hidden"): void;
  setWeather(on: boolean): void;
  setMonsoon(on: boolean): void;
}

export function mountTopRight(map: MlMap, cb: { onCountry: () => void; onWeather: () => void; onMonsoon: () => void; onStorms: () => void; onTheme: () => void }): TopRightHandles {
  const stack = document.createElement("div");
  stack.className = "tr-stack"; stack.setAttribute("role", "group"); stack.setAttribute("aria-label", "ปุ่มแผนที่");
  const mk = (id: string, icon: string, cap: string, aria: string): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button"; b.id = id; b.className = "tr-btn";
    b.innerHTML = `<span class="tr-ico" aria-hidden="true">${icon}</span><span class="tr-cap">${cap}</span>`;
    b.setAttribute("aria-label", aria);
    return b;
  };
  const country = mk("country-btn", "🇹🇭", "ทั้งประเทศ", "ดูทั้งประเทศ");
  // v0.9.12 — current weather: draws the live-storm track on demand (loaded only when tapped,
  // never at start-up) so the first screen stays smooth; a second tap hides it again.
  const weather = mk("weather-btn", "🌦", "สภาพอากาศ", "ดูสภาพอากาศ (พายุหมุนเขตร้อนใกล้ไทยตอนนี้)");
  weather.setAttribute("aria-pressed", "false");
  // v0.9.14 — monsoon & cold-air mass: a panel with an animation of the air flowing into Thailand in
  // the current regime's direction + the real wind/temperature behind it and the season's ENSO state.
  const monsoon = mk("monsoon-btn", "🌬", "ลมมรสุม", "ดูลมมรสุม/มวลอากาศเย็น และบริบทฤดูกาล (เอลนีโญ/ลานีญา)");
  monsoon.setAttribute("aria-pressed", "false");
  // v0.9.16 — every active tropical cyclone on Earth (spin + name on the map), each tagged by distance
  // from Thailand. The historical track simulation lives one level deeper, as a toggle inside the panel.
  const storm = mk("storm-sim-btn", "🌀", "พายุรอบโลก", "พายุรอบโลก (พายุหมุนเขตร้อนที่ยังมีผลอยู่ทั่วโลก พร้อมระยะห่างจากไทย)");
  const theme = mk("theme-btn", "☀️", "สว่าง", "เปลี่ยนเป็นโหมดสว่าง");
  // shows the mode a tap switches TO (☀️ สว่าง in dark mode, 🌙 มืด in light mode)
  const themeLabel = (): void => {
    theme.querySelector(".tr-ico")!.textContent = isLight() ? "🌙" : "☀️";
    theme.querySelector(".tr-cap")!.textContent = isLight() ? "มืด" : "สว่าง";
    theme.setAttribute("aria-label", isLight() ? "เปลี่ยนเป็นโหมดมืด" : "เปลี่ยนเป็นโหมดสว่าง");
  };
  themeLabel(); window.addEventListener("fs-theme", themeLabel);
  country.addEventListener("click", cb.onCountry);
  weather.addEventListener("click", cb.onWeather);
  monsoon.addEventListener("click", cb.onMonsoon);
  storm.addEventListener("click", cb.onStorms);
  theme.addEventListener("click", cb.onTheme);
  stack.append(country, weather, monsoon, storm, theme);
  // Keep live source credits inside the shared About disclosure on desktop and mobile.
  const attrib = map.getContainer().querySelector(".maplibregl-ctrl-attrib");
  const credits = document.getElementById("map-credits");
  if (attrib && credits) credits.append(attrib);
  document.body.append(stack);

  const setCountry = (mode: "country" | "back" | "hidden"): void => {
    country.hidden = mode === "hidden";
    const ico = country.querySelector(".tr-ico")!, cap = country.querySelector(".tr-cap")!;
    if (mode === "back") { ico.textContent = "📍"; cap.textContent = "รอบตัวฉัน"; country.setAttribute("aria-label", "กลับไปดูรอบตัวฉัน"); }
    else { ico.textContent = "🇹🇭"; cap.textContent = "ทั้งประเทศ"; country.setAttribute("aria-label", "ดูทั้งประเทศ"); }
    window.dispatchEvent(new Event("fs-top-layout"));
  };
  const setWeather = (on: boolean): void => {
    weather.classList.toggle("on", on);
    weather.setAttribute("aria-pressed", String(on));
    weather.setAttribute("aria-label", on ? "ซ่อนสภาพอากาศ (พายุหมุนเขตร้อน)" : "ดูสภาพอากาศ (พายุหมุนเขตร้อนใกล้ไทยตอนนี้)");
  };
  const setMonsoon = (on: boolean): void => {
    monsoon.classList.toggle("on", on);
    monsoon.setAttribute("aria-pressed", String(on));
  };
  return { country, weather, monsoon, storm, theme, stack, setCountry, setWeather, setMonsoon };
}
